import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CoursesService } from 'src/app/services/courses.service';
import { QuizService } from 'src/app/services/quiz.service';
import { CourseModulePage, LoggedInUser, QuizQuestion, UserCourseProgressWithModules } from 'src/app/models';
import { AuthService } from 'src/app/services/auth.service';
import { FlashMessagesService } from 'flash-messages-angular';
import { AppContextService, RouteService } from 'src/app/core/services';

@Component({
  selector: 'app-module-rw',
  templateUrl: './module-rw.component.html',
  styleUrls: ['./module-rw.component.css','../rentwise.component.css']
})
export class ModuleRwComponent implements OnInit {
  moduleNumber = 0;

  page = 1;
  loading = false;

  previewByAdmin = false;
  previewPage?: CourseModulePage;

  user!: LoggedInUser;

  // Last module, determines whether to show course complete or the module
  courseID = '';
  courseComplete = false;

  // Contains all questions for the module
  questions: QuizQuestion[] = [];

  constructor(
    private courseService: CoursesService,
    private auth: AuthService,
    private quizService: QuizService,
    private router: Router,
    private route: ActivatedRoute,
    private routeService: RouteService,
    private flashMessage: FlashMessagesService,
    private appContext: AppContextService
  ) {}

  get course() { return this.courseService.currentCourse as UserCourseProgressWithModules; }
  get module() { return this.course ? this.course.modules[this.moduleNumber - 1] : null; }
  get modulePages() { return this.module ? this.module.pages || [] : []; }
  get modulePage() { return this.modulePages[this.page - 1]; }

  get nextTitle() { return this.module?.endingQuiz && this.page >= this.modulePages.length ? 'Quiz' : 'Next'; }

  async ngOnInit(): Promise<void> {
    this.user = this.auth.user || {};

    this.courseService.setCurrentCourseBy('RentWise');

    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    // Super admin preview
    if (this.auth.isSuperAdmin && this.route.snapshot.data.preview) {
      this.previewByAdmin = true;
      this.appContext.update({ fastForward: true });
      this.loadPreviewCourseData();
    }
    // User course
    else {
      this.previewByAdmin = false;
      this.loadUserCourseData();
    }
  }

  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    this.content_keyup(event);
  }

  get delayNext(): boolean {
    if (this.previewByAdmin || this.appContext.data.fastForward) return false;

    return this.course.furthestModuleID === this.moduleNumber - 1
      ? this.course.furthestPageID <= this.page - 1
      : this.course.furthestModuleID <= this.moduleNumber - 1;
  }

  private loadUserCourseData() {
    if (this.auth.isSuperAdmin) return;

    this.courseID = this.course.courseID;
    this.moduleNumber = +this.route.snapshot.params.moduleNumber || 1;

    if (!this.courseService.currentCourse) {
      this.routeService.forceNavigate(['../..'], { relativeTo: this.route });
      return;
    }

    if (this.course.preTestRequired && !this.course.preTestPassed) {
      this.routeService.forceNavigate(['../../pre-evaluation'], { relativeTo: this.route });
      return;
    }

    // Check if current module is actually unlocked, otherwise redirect to the furthest one
    if (this.moduleNumber > 1 && this.moduleNumber > this.course.furthestModuleID + 1) {
      this.moduleNumber = this.course.furthestModuleID + 1;
      this.routeService.forceNavigate(['..', this.moduleNumber], { relativeTo: this.route });
    }

    // Check if module found, otherwise fallback to 1st one
    if (this.moduleNumber > 1 && !this.module) {
      this.moduleNumber = 1;
      this.routeService.forceNavigate(['..', this.moduleNumber], { relativeTo: this.route })
    }

    // Open the furthest page
    if (this.course.furthestModuleID === this.moduleNumber - 1) {
      this.page = this.course.furthestPageID + 1;

      if (this.module && this.page > 1 && this.page >= this.module.pages.length) {
        if (!this.module.endingQuiz) {
          this.courseComplete = true;
        }
        else {
          this.page--;
        }
      }
    }

    this.loadQuizQuestions();
  }

  private loadPreviewCourseData() {
    if (!this.auth.isSuperAdmin) return;

    this.courseID = (this.course as any).title;

    const moduleID = this.route.snapshot.params.moduleID;
    const moduleNumber = Number(moduleID) || 0;

    if (moduleNumber > 0) {
      this.moduleNumber = moduleNumber;
    }
    else if (moduleID) {
      this.moduleNumber = this.course.modules.find(m => m.moduleID === moduleID)?.moduleNumber || this.moduleNumber;
    }

    if (!(this.moduleNumber > 0)) {
      this.showError('Module not found.');
      this.goBackToAdmin();
      return;
    }

    this.previewPage = this.modulePages.find(page => page.pageID === this.route.snapshot.queryParams.pageid);

    if (!this.previewPage) {
      // this.showError('Page not found.');
      this.goBackToAdmin();
      return;
    }

    this.page = this.previewPage.pageNumber;

    this.loadQuizQuestions();
  }

  private async loadQuizQuestions() {
    // Get the questions for this module and assign them to this.questions
    if (this.module?.endingQuiz) {
      this.quizService.getModuleQuizzes(this.module.moduleID).toPromise().then((res: any) => {
        this.quizService.moduleQuizQuestions = res.docs;
        this.questions = res.docs;
      });
    }
    else {
      this.quizService.moduleQuizQuestions = [];
      this.questions = [];
    }
  }

  // Update furthestModuleID
  async updateProgress() {
    if (this.previewByAdmin) return;

    const postTestRequired = this.course.postTestRequired
      ? !this.course.postTestPassed
      : false;

    if (this.course.furthestModuleID < this.moduleNumber) {
      await this.courseService.updateFurthestModule().toPromise();
    }

    // Post-evaluation required
    if (this.moduleNumber >= this.course.modules.length && postTestRequired) {
      this.routeService.forceNavigate([`../../post-evaluation`], { relativeTo: this.route });
      return;
    }

    // Course is complete
    if (this.moduleNumber >= this.course.modules.length && !postTestRequired) {
      this.courseComplete = true;
      this.markCourseAsCompleted();
      return;
    }

    // Navigate to next module
    this.routeService.forceNavigate(['..', this.moduleNumber + 1], { relativeTo: this.route });
  }

  // Update furthestPageID
  async updatePageProgress() {
    if (this.previewByAdmin) return;

    if (this.course.furthestModuleID === this.moduleNumber - 1) {
      if (this.course.furthestPageID < this.page) {
        await this.courseService.updateFurthestPage(this.page).toPromise();
      }
    }
  }

  // Handle the quiz results, allow them to pass or not
  handleQuiz(valueEmitted: boolean){

    // Quiz passed
    if (valueEmitted) {
      if (this.previewByAdmin) {
        this.goBackToAdmin();
        return;
      }

      this.updateProgress();
    }

    // Review
    else {
      this.setPage(1);
    }
  }

  next(event?: Event) {
    if (event?.defaultPrevented) return false;
    if (this.module && !this.module.endingQuiz && this.moduleNumber >= this.course.modules.length && this.page >= this.modulePages.length) {
      this.courseComplete = true;
      this.markCourseAsCompleted();
    }
    this.incPage(1);
    this.scrollToTop();
    this.updatePageProgress();
    return true;
  }

  back(event?: Event) {
    if (event?.defaultPrevented) return false;

    if (this.module && this.page === 1) {
      if (this.previewByAdmin) {
        const courseid = this.courseID;
        this.routeService.forceNavigate(['/admin/admin-courses'], { queryParams: { courseid } });
        return true;
      }
      else {
        this.routeService.forceNavigate(['/courses', this.courseID]);
        return true;
      }
    }

    this.incPage(-1);
    this.scrollToTop();
    return true;
  }

  backFromCompleted(event?: Event) {
    this.courseComplete = false;

    if (this.page > this.modulePages.length && !this.module?.endingQuiz) {
      this.back(event);
    }
  }

  private async markCourseAsCompleted() {
    this.course.isCompleted = true;
    this.course.completionDate = new Date();
    this.course.furthestModuleID = this.course.modules.length + 1;
    this.course.furthestPageID = 0;

    await this.courseService.markAsCompleted().toPromise();
  }

  setPage(value: number) {
    this.loading = true;

    setTimeout(() => {
      this.page = value;
      this.loading = false;
    });
  }

  incPage(value: number) {
    this.setPage(this.page + value);
  }

  goBackToAdmin() {
    if (this.previewByAdmin) {
      const courseid = this.courseID;
      const moduleid = this.module?.moduleID;
      this.routeService.forceNavigate(['/admin/admin-courses'], { queryParams: { courseid, moduleid } });
    }
  }

  private showError(message: string) {
    console.warn(message);
    this.flashMessage.show(message, {
      cssClass: 'alert-danger',
      timeout: 3000
    });
  }

  content_keyup(event: KeyboardEvent) {
    if (event.defaultPrevented) return;

    if ((this.auth.isSuperAdmin || this.appContext.data.fastForward) && event.key === '>' && this.page <= this.modulePages.length) {
      event.preventDefault();
      this.next();
    }

    if ((this.auth.isSuperAdmin || this.appContext.data.fastForward) && event.key === '<' && this.page >= 1) {
      event.preventDefault();
      this.back();
    }
  }

  editPage() {
    if (this.previewByAdmin) {
      const courseid = this.courseID;
      const moduleid = this.module?.moduleID;
      const pageid   = this.modulePage?.pageID;
      this.routeService.forceNavigate(['/admin/admin-courses/edit-page'], { queryParams: { courseid, moduleid, pageid } });
    }
  }

  private scrollToTop() {
    setTimeout(() => this.routeService.scrollToTop());
  }
}
